import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Check, Clock, Sparkles, Heart, BookOpen, Feather, Calendar, Gift, PartyPopper, Flame } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { RELATIONSHIP_OPTIONS, type AnnouncementFormData, type RelationshipType, type TemplateType } from '@/types/announcement';
import { useRecommendedRecipients } from '@/hooks/useRecommendedRecipients';
import { getAutoTypography } from '@/lib/autoAssign';
import { LiveTemplatePreview } from '@/components/wizard/LiveTemplatePreview';

const copy = {
  en: { title: 'Who is this for?', subtitle: "Let's start with who and why.", nameLabel: 'Their name or what you call them', placeholder: 'e.g., Mom, Dad, Grandma Rose...', relLabel: 'Your relationship', catLabel: 'What do you want to say?' },
  es: { title: '¿Para quién es esto?', subtitle: 'Empecemos con quién y por qué.', nameLabel: 'Su nombre o cómo le llamas', placeholder: 'ej., Mamá, Papá, Abuela Rosa...', relLabel: 'Tu relación', catLabel: '¿Qué quieres decir?' },
  fr: { title: 'Pour qui est-ce ?', subtitle: 'Commençons par qui et pourquoi.', nameLabel: 'Leur nom ou surnom', placeholder: 'ex., Maman, Papa, Grand-mère...', relLabel: 'Votre relation', catLabel: 'Que voulez-vous dire ?' },
  pt: { title: 'Para quem é isto?', subtitle: 'Vamos começar com quem e porquê.', nameLabel: 'Nome ou apelido', placeholder: 'ex., Mãe, Pai, Vovó Rosa...', relLabel: 'Seu relacionamento', catLabel: 'O que você quer dizer?' },
  ar: { title: 'لمن هذا؟', subtitle: 'لنبدأ بمن ولماذا.', nameLabel: 'اسمهم أو ما تناديهم', placeholder: 'مثال: ماما، بابا، جدتي...', relLabel: 'علاقتك', catLabel: 'ماذا تريد أن تقول؟' },
  ja: { title: 'これは誰のためですか？', subtitle: '誰に、なぜ伝えたいですか。', nameLabel: '相手の名前や呼び名', placeholder: '例：お母さん、お父さん...', relLabel: 'あなたの関係', catLabel: '何を伝えたいですか？' },
  ko: { title: '누구를 위한 것인가요?', subtitle: '누구에게, 왜 전하고 싶은지 시작해 봅시다.', nameLabel: '상대방의 이름이나 호칭', placeholder: '예: 엄마, 아빠, 할머니...', relLabel: '당신의 관계', catLabel: '무엇을 말하고 싶나요?' },
  id: { title: 'Untuk siapa ini?', subtitle: 'Mari mulai dengan siapa dan mengapa.', nameLabel: 'Nama atau panggilan mereka', placeholder: 'contoh: Mama, Papa, Nenek...', relLabel: 'Hubungan Anda', catLabel: 'Apa yang ingin Anda katakan?' },

  hi: { title: 'यह किसके लिए है?', subtitle: "Let's start with who and why.", nameLabel: 'उनका नाम या आप उन्हें क्या कहते हैं', placeholder: 'उदाहरण के लिए, माँ, पिताजी, दादी रोज़...', relLabel: 'आपका रिश्ता', catLabel: 'आप क्या कहना चाहते हैं?' },
};

const relLabels: Record<string, Record<string, string>> = {
  parent: { en: 'Parent', es: 'Padre/Madre', fr: 'Parent', pt: 'Pai/Mãe', ar: 'والد/ة', ja: '親', ko: '부모', id: 'Orang Tua' },
  grandparent: { en: 'Grandparent', es: 'Abuelo/a', fr: 'Grand-parent', pt: 'Avô/Avó', ar: 'جد/جدة', ja: '祖父母', ko: '조부모', id: 'Kakek/Nenek' },
  sibling: { en: 'Sibling', es: 'Hermano/a', fr: 'Frère/Sœur', pt: 'Irmão/Irmã', ar: 'أخ/أخت', ja: 'きょうだい', ko: '형제자매', id: 'Saudara' },
  child: { en: 'Child', es: 'Hijo/a', fr: 'Enfant', pt: 'Filho/a', ar: 'طفل', ja: '子ども', ko: '자녀', id: 'Anak' },
  spouse: { en: 'Spouse/Partner', es: 'Cónyuge', fr: 'Conjoint(e)', pt: 'Cônjuge', ar: 'زوج/ة', ja: '配偶者', ko: '배우자', id: 'Pasangan' },
  friend: { en: 'Friend', es: 'Amigo/a', fr: 'Ami(e)', pt: 'Amigo/a', ar: 'صديق/ة', ja: '友人', ko: '친구', id: 'Teman' },
  mentor: { en: 'Mentor/Teacher', es: 'Mentor', fr: 'Mentor', pt: 'Mentor', ar: 'معلم/مرشد', ja: '師', ko: '멘토', id: 'Mentor' },
  colleague: { en: 'Colleague', es: 'Colega', fr: 'Collègue', pt: 'Colega', ar: 'زميل', ja: '同僚', ko: '동료', id: 'Rekan' },
  crush: { en: 'Crush / Secret Admiration', es: 'Enamoramiento Secreto', fr: 'Admiration Secrète', pt: 'Paixão Secreta', ar: 'إعجاب سري', ja: '秘密の想い', ko: '짝사랑', id: 'Kekaguman Rahasia' },
  other: { en: 'Other', es: 'Otro', fr: 'Autre', pt: 'Outro', ar: 'آخر', ja: 'その他', ko: '기타', id: 'Lainnya' },
};

const CATEGORY_OPTIONS: { value: TemplateType; icon: React.ComponentType<{ className?: string }>; gradient: string }[] = [
  { value: 'love', icon: Heart, gradient: 'from-rose-100 to-rose-50' },
  { value: 'gratitude', icon: Sparkles, gradient: 'from-amber-100 to-amber-50' },
  { value: 'apology', icon: Feather, gradient: 'from-slate-100 to-slate-50' },
  { value: 'closure', icon: BookOpen, gradient: 'from-sky-100 to-sky-50' },
  { value: 'birthday', icon: Gift, gradient: 'from-purple-100 to-purple-50' },
  { value: 'anniversary', icon: Calendar, gradient: 'from-yellow-100 to-yellow-50' },
  { value: 'celebration', icon: PartyPopper, gradient: 'from-green-100 to-green-50' },
  { value: 'encouragement', icon: Flame, gradient: 'from-emerald-100 to-cyan-50' },
  { value: 'custom', icon: Sparkles, gradient: 'from-primary/10 to-primary/5' },
];

const catLabels: Record<string, Record<string, string>> = {
  love: { en: 'Love', es: 'Amor', fr: 'Amour', pt: 'Amor', ar: 'حب', ja: '愛', ko: '사랑', id: 'Cinta' },
  gratitude: { en: 'Gratitude', es: 'Gratitud', fr: 'Gratitude', pt: 'Gratidão', ar: 'امتنان', ja: '感謝', ko: '감사', id: 'Terima Kasih' },
  apology: { en: 'Apology', es: 'Disculpa', fr: 'Excuses', pt: 'Desculpas', ar: 'اعتذار', ja: '謝罪', ko: '사과', id: 'Maaf' },
  closure: { en: 'Closure', es: 'Cierre', fr: 'Clôture', pt: 'Encerramento', ar: 'إغلاق', ja: '区切り', ko: '마무리', id: 'Penutupan' },
  birthday: { en: 'Birthday', es: 'Cumpleaños', fr: 'Anniversaire', pt: 'Aniversário', ar: 'عيد ميلاد', ja: '誕生日', ko: '생일', id: 'Ulang Tahun' },
  anniversary: { en: 'Anniversary', es: 'Aniversario', fr: 'Anniversaire', pt: 'Aniversário', ar: 'ذكرى', ja: '記念日', ko: '기념일', id: 'Hari Jadi' },
  celebration: { en: 'Celebration', es: 'Celebración', fr: 'Célébration', pt: 'Celebração', ar: 'احتفال', ja: 'お祝い', ko: '축하', id: 'Perayaan' },
  encouragement: { en: 'Encouragement', es: 'Ánimo', fr: 'Encouragement', pt: 'Encorajamento', ar: 'تشجيع', ja: '励まし', ko: '격려', id: 'Semangat' },
  custom: { en: 'Custom', es: 'Personalizado', fr: 'Personnalisé', pt: 'Personalizado', ar: 'مخصص', ja: 'カスタム', ko: '맞춤', id: 'Kustom' },
};

interface CombinedEntryStepProps {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
}

export function CombinedEntryStep({ formData, updateFormData }: CombinedEntryStepProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const { recipients } = useRecommendedRecipients(formData.relationship);

  const handleSelectRecipient = (name: string, relationship: RelationshipType | null) => {
    const updates: Partial<AnnouncementFormData> = { recipientName: name };
    if (relationship) updates.relationship = relationship;
    updateFormData(updates);
  };

  const handleCategorySelect = (value: TemplateType) => {
    const typo = getAutoTypography(value);
    updateFormData({
      template: value,
      customSettings: { ...formData.customSettings, typographyPreset: typo },
    });
  };

  const filteredRecipients = formData.recipientName.trim()
    ? recipients.filter((r) =>
        r.recipientName.toLowerCase().includes(formData.recipientName.toLowerCase()) &&
        r.recipientName.toLowerCase() !== formData.recipientName.toLowerCase()
      )
    : recipients;

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 text-primary mb-3">
          <Users className="h-7 w-7" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">{c.title}</h2>
        <p className="text-sm text-muted-foreground">{c.subtitle}</p>
      </div>

      {/* Name input */}
      <div className="max-w-md mx-auto space-y-3">
        <Label htmlFor="recipientName" className="text-sm font-medium">{c.nameLabel}</Label>
        <Input
          id="recipientName"
          placeholder={c.placeholder}
          value={formData.recipientName}
          onChange={(e) => updateFormData({ recipientName: e.target.value })}
          className="text-lg py-6"
        />

        {/* Suggested recipients */}
        <AnimatePresence>
          {filteredRecipients.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-2">
                {filteredRecipients.slice(0, 4).map((r) => {
                  const relEmoji = RELATIONSHIP_OPTIONS.find((o) => o.value === r.relationship)?.emoji || '💌';
                  return (
                    <motion.button
                      key={r.recipientName}
                      type="button"
                      onClick={() => handleSelectRecipient(r.recipientName, r.relationship)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 bg-muted/30 hover:border-primary/50 hover:bg-primary/5 transition-all text-sm"
                      whileTap={{ scale: 0.97 }}
                    >
                      <span>{relEmoji}</span>
                      <span className="font-medium text-foreground">{r.recipientName}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Relationship icons grid */}
      <div className="max-w-2xl mx-auto">
        <Label className="text-sm font-medium mb-3 block text-center">{c.relLabel}</Label>
        <div className="grid grid-cols-5 gap-2">
          {RELATIONSHIP_OPTIONS.map((option) => {
            const isSelected = formData.relationship === option.value;
            const localLabel = relLabels[option.value]?.[language] || relLabels[option.value]?.en || option.label;
            return (
              <motion.button
                key={option.value}
                onClick={() => updateFormData({ relationship: option.value })}
                className={cn(
                  'flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all',
                  isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border/50 hover:border-primary/40'
                )}
                whileTap={{ scale: 0.95 }}
              >
                {isSelected && (
                  <motion.div className="absolute -top-1 -right-1" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                    <Check className="h-3 w-3 text-primary" />
                  </motion.div>
                )}
                <span className="text-xl">{option.emoji}</span>
                <span className={cn('text-[10px] font-medium leading-tight text-center', isSelected ? 'text-primary' : 'text-muted-foreground')}>
                  {localLabel}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Category horizontal scroll cards */}
      <div className="max-w-2xl mx-auto">
        <Label className="text-sm font-medium mb-3 block text-center">{c.catLabel}</Label>
        <div className="flex gap-3 overflow-x-auto pb-2 px-1 snap-x snap-mandatory scrollbar-none">
          {CATEGORY_OPTIONS.map((cat) => {
            const isSelected = formData.template === cat.value;
            const Icon = cat.icon;
            const label = catLabels[cat.value]?.[language] || catLabels[cat.value]?.en || cat.value;
            return (
              <motion.button
                key={cat.value}
                onClick={() => handleCategorySelect(cat.value)}
                className={cn(
                  'flex-shrink-0 snap-center flex flex-col items-center gap-2 w-20 py-3 px-2 rounded-xl border-2 transition-all',
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border/50 hover:border-primary/40'
                )}
                whileTap={{ scale: 0.95 }}
              >
                <div className={cn(
                  'h-10 w-10 rounded-full flex items-center justify-center bg-gradient-to-br',
                  cat.gradient
                )}>
                  <Icon className={cn('h-5 w-5', isSelected ? 'text-primary' : 'text-foreground/70')} />
                </div>
                <span className={cn('text-xs font-medium', isSelected ? 'text-primary' : 'text-foreground')}>
                  {label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Subtle animated preview when category is selected */}
      <AnimatePresence>
        {formData.template && formData.recipientName.trim() && (
          <motion.div
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 10, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
            className="max-w-sm mx-auto overflow-hidden"
          >
            <LiveTemplatePreview formData={formData} className="min-h-[160px] scale-90 origin-top" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
